import Box from "@mui/material/Box";
import { makeStyles } from "@mui/styles";
import { IMessage } from "src/app/types";
import ListItem from "@mui/material/ListItem";
import Typography from "@mui/material/Typography";
import ChatImageSuspense from "./components/ChatImageSuspense";
import { useSocketStore } from "app/store/socketStore";
import { getUrlParams, getSalesContext } from "app/store/dataUtils";
import ChatsFormatTexts from "./components/ChatsFormatTexts";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { IImages } from "src/app/interfaces/chat-image";

const useStyles = makeStyles({
  chatSection: {
    width: "100%",
    height: "80vh",
  },
  headBG: {
    // backgroundColor: '#f8f8f8',
  },
  borderRight500: {
    borderRight: "1px solid #e0e0e0",
  },
  messageArea: {
    // width: '100vw',
    position: "relative",
    height: "70vh",
    // backgroundColor: '#eeeeee',
    overflowY: "auto",
    scrollbarWidth: "none" /* For Firefox */,
    "&::-webkit-scrollbar": {
      display: "none" /* For Chrome, Safari, and Opera */,
    },
  },
  messageInput: {
    padding: "20px",
    // borderRadius: '12px',
  },
  messageBubble: {
    maxWidth: "70%",
    margin: "10px",
    padding: "10px",
    gap: ".5rem",
    minHeight: "60px",
    position: "relative",
    lineHeight: "1.25rem",
    display: "flex",
    flexDirection: "column",
    wordBreak: "break-word",
    // overflow: 'hidden',
    textOverflow: "ellipsis",
  },
  messageBubbleLeft: {
    borderBottomRightRadius: "var(--rc-radius-bubble, 12px)",
    borderTopRightRadius: "var(--rc-radius-bubble, 12px)",
    borderBottomLeftRadius: "var(--rc-radius-bubble, 12px)",
    borderTopLeftRadius: "0px",
    backgroundColor: "var(--rc-bubble-in-bg, #EFF0F4)",
    color: "var(--rc-bubble-in-ink, #44454D)",
    marginLeft: "10px",
    marginTop: "25px",
    padding: "10px 15px",
  },
  messageBubbleRight: {
    borderBottomRightRadius: "var(--rc-radius-bubble, 12px)",
    borderBottomLeftRadius: "var(--rc-radius-bubble, 12px)",
    borderTopLeftRadius: "var(--rc-radius-bubble, 12px)",
    borderTopRightRadius: "0px",
    minWidth: "102px",
    backgroundColor: "var(--rc-chrome, #010644)",
    color: "#fff",
    marginRight: "10px",
    marginLeft: "auto",
    marginTop: "25px",
    padding: "10px 15px",
  },
});

const seenDoubleTick = (
  <svg
    width="17px"
    height="17px"
    viewBox="0 0 16 16"
    xmlns="http://www.w3.org/2000/svg"
    version="1.1"
    fill="none"
    stroke="#B3B4C7"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="1.5"
    className="mb-3"
  >
    <path d="m1.75 9.75 2.5 2.5m3.5-4 2.5-2.5m-4.5 4 2.5 2.5 6-6.5" />
  </svg>
);
const unseenDoubleTick = (
  <svg
    width="17px"
    height="17px"
    className="relative bottom-1"
    viewBox="0 0 16 16"
    xmlns="http://www.w3.org/2000/svg"
    version="1.1"
    fill="none"
    stroke="#B3B4C7"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="1.5"
  >
    <polyline points="2.75 8.75,6.25 12.25,13.25 4.75" />
  </svg>
);

interface ChatMessageProps {
  idx: number | string;
  message: Partial<IMessage>;
  // fetchImages(messageId: string): Promise<void>
}

export default function ChatMessageSuspense({
  idx,
  message,
}: ChatMessageProps) {
  const [sending, setSending] = useState(message?.sending);
  const { shipment, language } = getUrlParams();
  const [failed, setFailed] = useState(false);
  const { messageSocket } = useSocketStore();
  const classes = useStyles();

  const renderMessageStatus = () => {
    if (failed && !sending) {
      return (
        <FuseSvgIcon className="text-48 text-red" size={16} color="action">
          heroicons-outline:exclamation-circle
        </FuseSvgIcon>
      );
    }
    if (sending && !failed) {
      return (
        <FuseSvgIcon className="text-48 text-gray-400" size={16} color="action">
          heroicons-outline:clock
        </FuseSvgIcon>
      );
    }
    if (!sending && !failed) {
      return message?.seen ? seenDoubleTick : unseenDoubleTick;
    }
    return null;
  };

  const getMessageBubbleClass = (
    source: string,
    shipment: string | undefined,
  ) => {
    // Sales mode: "my" side comes from the token role
    const salesContext = getSalesContext();
    if (salesContext) {
      return source === salesContext.role
        ? classes.messageBubbleRight
        : classes.messageBubbleLeft;
    }

    if (!shipment && source === "WAREHOUSE") {
      return classes.messageBubbleRight;
    } else if (shipment && source === "DRIVER") {
      return classes.messageBubbleRight;
    } else {
      return classes.messageBubbleLeft;
    }
  };

  useEffect(() => {
    if (!messageSocket || !message._id) return;

    // Define all event handlers
    const handleMessageCreateFailed = () => {
      console.log(`Message create failed: ${message._id}`);
      setFailed(true);
      setSending(false);
    };

    const handleImageCreateFailed = () => {
      console.log(`Image create failed: ${message._id}`);
      setFailed(true);
      setSending(false);
    };

    const handleMessageCreated = () => {
      console.log(`Message created: ${message._id}`);
      setSending(false);
    };

    const handleImageCreated = () => {
      console.log(`Image created: ${message._id}`);
      setSending(false);
    };

    const handleImageFetchFailed = () => {
      console.log(`Image fetch failed: ${message._id}`);
      setFailed(true);
    };

    // Event names
    const messageCreateFailedEvent = `message-create-failed-${message._id}`;
    const imageCreateFailedEvent = `image-create-failed-${message._id}`;
    const messageCreatedEvent = `message-created-${message._id}`;
    const imageCreatedEvent = `image-created-${message._id}`;
    const imageFetchFailedEvent = `image-fetch-failed-${message._id}`;

    // Register all listeners
    messageSocket.on(messageCreateFailedEvent, handleMessageCreateFailed);
    messageSocket.on(imageCreateFailedEvent, handleImageCreateFailed);
    messageSocket.on(messageCreatedEvent, handleMessageCreated);
    messageSocket.on(imageCreatedEvent, handleImageCreated);
    messageSocket.on(imageFetchFailedEvent, handleImageFetchFailed);

    // Clean up all listeners on unmount or when dependencies change
    return () => {
      messageSocket.off(messageCreateFailedEvent, handleMessageCreateFailed);
      messageSocket.off(imageCreateFailedEvent, handleImageCreateFailed);
      messageSocket.off(messageCreatedEvent, handleMessageCreated);
      messageSocket.off(imageCreatedEvent, handleImageCreated);
      messageSocket.off(imageFetchFailedEvent, handleImageFetchFailed);
    };
  }, [messageSocket, message._id]);

  return (
    <ListItem className="flex flex-row items-start" key={idx}>
      {message?.source == "CUSTOMER" && shipment && (
        <span className="bg-[color:var(--rc-bubble-in-bg,#EFEFEF)] relative top-10 grid place-items-center text-[color:var(--rc-chrome,#010644)] text-[12px] font-500 min-w-[40px] max-w-[40px] h-[40px] rounded-full p-5">
          CU
        </span>
      )}
      {message?.source == "DRIVER" && !shipment && (
        <span className="bg-[color:var(--rc-bubble-in-bg,#EFEFEF)] relative top-10 grid place-items-center text-[color:var(--rc-chrome,#010644)] text-[12px] font-500 min-w-[40px] max-w-[40px] h-[40px] rounded-full p-5">
          DR
        </span>
      )}
      <Box
        className={`${classes.messageBubble} ${getMessageBubbleClass(message?.source, shipment)}`}
        style={{ wordWrap: "break-word" }}
      >
        {message && (
          <>
            {message.contains_images && (
              <ChatImageSuspense
                key={`image-${message._id}`}
                idx={`${idx}-${message._id}`}
                messageId={message._id}
                messageSocket={messageSocket}
                initialSend={
                  !!message.chat_images &&
                  Array.isArray(message.chat_images) &&
                  message.chat_images.length > 0
                }
                initialImage={
                  Array.isArray(message.chat_images) ? message.chat_images : []
                }
                source={message.source}
                // fetchImages={fetchImages}
              />
            )}
            {message.contains_location &&
              message.location &&
              (() => {
                const isRight =
                  (!shipment && message.source === "WAREHOUSE") ||
                  (shipment && message.source === "DRIVER");
                return (
                  <Box
                    className="inline-flex items-center gap-8 rounded-xl px-16 py-10 cursor-pointer"
                    onClick={() =>
                      window.open(
                        `https://www.google.com/maps?q=${message.location.lat},${message.location.long}`,
                        "_blank"
                      )
                    }
                    sx={{
                      background: isRight
                        ? "rgba(255,255,255,0.15)"
                        : "#dfe0e8",
                      color: isRight ? "#fff" : "#44454D",
                      fontSize: "13px",
                      fontWeight: 500,
                      "&:hover": { opacity: 0.8 },
                    }}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      style={{
                        color: isRight ? "#a5b4fc" : "var(--rc-chrome, #010644)",
                        flexShrink: 0,
                      }}
                    >
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z" />
                    </svg>
                    <span>Tap to view location</span>
                  </Box>
                );
              })()}
            {message.text && <ChatsFormatTexts messageText={message.text} />}
          </>
        )}

        <Typography
          className={`h-12 text-[10px] ${message.source === "WAREHOUSE" ? "text-[#c5c5c5] place-content-end" : "text-[#656669] place-self-start"} w-full flex flex-row items-center gap-4`}
          variant="caption"
        >
          {dayjs(message?.created_at).locale(language).format("hh:mm A")}
          {message?.source == "WAREHOUSE" && !shipment && (
            <>{renderMessageStatus()}</>
          )}
        </Typography>
      </Box>
      {message?.source == "WAREHOUSE" && !shipment && (
        <span className="bg-[color:var(--rc-bubble-in-bg,#EFEFEF)] relative top-10 grid place-items-center text-[color:var(--rc-chrome,#010644)] text-[12px] font-500 min-w-[40px] max-w-[40px] h-[40px] rounded-full p-5">
          {" "}
          WH
        </span>
      )}
      {message?.source == "DRIVER" && shipment && (
        <span className="bg-[color:var(--rc-bubble-in-bg,#EFEFEF)] relative top-10 grid place-items-center text-[color:var(--rc-chrome,#010644)] text-[12px] font-500 min-w-[40px] max-w-[40px] h-[40px] rounded-full p-5">
          {" "}
          DR
        </span>
      )}
    </ListItem>
  );
}
